import { HugeiconsIcon } from '@hugeicons/react';
import {
  DatabaseIcon,
  Add01Icon,
  FloppyDiskIcon,
  Cancel01Icon,
  Loading02Icon,
  AlertCircleIcon,
  RefreshIcon,
  Edit02Icon,
  ArrowLeft01Icon,
  Tick02Icon,
  SparklesIcon,
  UserGroupIcon,
} from '@hugeicons/core-free-icons';
import { Icon } from '@iconify/react';
import { useEffect, useState } from 'react';
import { api, apiError } from '@/lib/api';
import { PageHeader } from '@/components/common/Bits';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandItem } from '@/components/ui/command';
import { MotionSection } from '@/components/shared/MotionSection';
import { toast } from 'sonner';
import CatalogTree from '@/components/insights/CatalogTree';
import MarkdownEditor from '@/components/insights/MarkdownEditor';
import GroupsManager from '@/components/insights/GroupsManager';
import AgentAccessPanel from '@/components/insights/AgentAccessPanel';
import { selectionToResources, resourcesToSelection } from '@/components/insights/selection';
// Registra OFFLINE los logos de proveedor de modelo (import con efecto) y expone
// el mapa proveedor -> nombre de icono Iconify. Iconify se usa SOLO para estos
// logos; Hugeicons sigue siendo la familia de iconos de la UI.
import { PROVIDER_ICON } from '@/lib/providerIcons';

// InsightsAgents — pantalla unica de VIA Insights para configurar agentes. Un
// agente equivale a un grupo: define a que datos (esquemas/tablas/columnas) puede
// acceder y como debe comportarse (instrucciones, reglas de formato, skills).
// Dos vistas: LISTA de agentes y EDICION (tabs Datos / Instrucciones).
//
// Consume el contrato canonico:
//   GET  /insights/agents       (lista)
//   POST /insights/agents       (crear)
//   PUT  /insights/agents/{id}  (editar)
// El catalogo y las descripciones los maneja CatalogTree.

// KNOWN_MODELS — modelos disponibles para el agente, con nombres comerciales
// legibles mapeados a los IDs de inferencia de Amazon Bedrock en us-east-1.
// La lista es CURADA MANUALMENTE porque aún no existe un endpoint que exponga
// los modelos habilitados; se incluyen solo IDs de los que se tiene certeza
// (perfiles de inferencia "us." vigentes). Se guarda el id en default_model.
// El campo `provider` mapea cada modelo a su logo (ver PROVIDER_ICON). Hoy todos
// son Anthropic; cuando se habiliten modelos de otros proveedores (gemini,
// openai, aws) basta añadir entradas con el proveedor correspondiente.
const KNOWN_MODELS = [
  { label: 'Claude Opus 4.1', id: 'us.anthropic.claude-opus-4-1-20250805-v1:0', provider: 'anthropic' },
  { label: 'Claude Sonnet 4.5', id: 'us.anthropic.claude-sonnet-4-5-20250929-v1:0', provider: 'anthropic' },
  { label: 'Claude Sonnet 3.7', id: 'us.anthropic.claude-3-7-sonnet-20250219-v1:0', provider: 'anthropic' },
  { label: 'Claude Sonnet 3.5 v2', id: 'us.anthropic.claude-3-5-sonnet-20241022-v2:0', provider: 'anthropic' },
  { label: 'Claude Haiku 3.5', id: 'us.anthropic.claude-3-5-haiku-20241022-v1:0', provider: 'anthropic' },
];

// modelDisplay — resuelve el id técnico guardado en default_model a su nombre
// comercial legible y su proveedor. Si el id no está en la lista curada (valor
// legacy o manual), degrada mostrando el id tal cual, sin proveedor. Evita que
// la tarjeta muestre cadenas como "us.anthropic.claude-opus-4-1-20250805-v1:0".
function modelDisplay(modelId) {
  if (!modelId) return null;
  const known = KNOWN_MODELS.find((m) => m.id === modelId);
  return known ? { label: known.label, provider: known.provider } : { label: modelId, provider: null };
}

// ModelOption — nombre del modelo con el logo de su proveedor delante. Si el
// proveedor no tiene logo registrado (o el modelo no define proveedor), cae a un
// icono Hugeicons neutro para no romper la UI. El icono es decorativo
// (aria-hidden); el texto accesible es el nombre comercial.
function ModelOption({ provider, label }) {
  const iconName = provider ? PROVIDER_ICON[provider] : undefined;
  return (
    <span className="flex items-center gap-2">
      {iconName ? (
        <Icon icon={iconName} width={16} height={16} aria-hidden />
      ) : (
        <HugeiconsIcon icon={SparklesIcon} size={16} strokeWidth={1.5} aria-hidden="true" />
      )}
      {label}
    </span>
  );
}

// accumulateSkillPages — función pura que reduce las páginas acumuladas del
// toolkit en la lista final de skills. Se extrae para poder testear la lógica de
// corte si en el futuro se añade un framework de test (design.md C.4).
export function accumulateSkillPages(pages) {
  return pages.reduce((acc, items) => acc.concat(Array.isArray(items) ? items : []), []);
}

// Tope defensivo de páginas: a 20 items/página son 1000 skills. Evita colgar la
// UI si `total` viene inconsistente. Para catálogos mayores, el backlog es un
// filtro server-side (?q=) en /toolkit/skills (design.md C.1).
const MAX_SKILL_PAGES = 50;
const SKILLS_PAGE_LIMIT = 20; // el backend fija limit=20; se refleja aquí para el bucle

// El acceso a los agentes ya no se gestiona con grupos de texto libre: la
// pestaña «Grupos» (GroupsManager) administra grupos reales de usuarios y el
// bloque «Acceso» (AgentAccessPanel) concede acceso por grupo o por persona.
// El campo legacy insights_domains.groups queda deprecado y no se escribe.

// SkillsCombobox — selector buscable de skills del registro toolkit. Reemplaza
// los antiguos checkboxes (y la sección MCP) por un combobox shadcn
// (command + popover): un botón abre un popover con buscador cliente (cmdk) y una
// lista de skills; cada selección se refleja como chip removible debajo. El
// identificador que se persiste sigue siendo item.id (sin cambios en el body de
// POST/PUT). Cubre los estados cargando/error/vacío del registro.
function SkillsCombobox({ state, skills, selected, onToggle, onRemove, onRetry }) {
  const [open, setOpen] = useState(false);

  if (state.loading) {
    return (
      <fieldset className="flex flex-col gap-2">
        <legend className="text-sm font-medium text-foreground">Skills disponibles</legend>
        <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground" role="status" aria-live="polite">
          <HugeiconsIcon icon={Loading02Icon} size={18} strokeWidth={1.5} className="animate-spin" aria-hidden="true" />
          Cargando skills…
        </div>
      </fieldset>
    );
  }

  if (state.error) {
    return (
      <fieldset className="flex flex-col gap-2">
        <legend className="text-sm font-medium text-foreground">Skills disponibles</legend>
        <div className="flex items-center gap-2 rounded-lg bg-destructive/10 p-3 text-sm text-destructive" role="alert">
          <HugeiconsIcon icon={AlertCircleIcon} size={18} strokeWidth={1.5} aria-hidden="true" />
          <span className="flex-1">{state.error}</span>
          <button
            type="button"
            onClick={onRetry}
            className="inline-flex items-center gap-1 font-medium hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            <HugeiconsIcon icon={RefreshIcon} size={14} strokeWidth={1.5} aria-hidden="true" />
            Reintentar
          </button>
        </div>
      </fieldset>
    );
  }

  const byId = new Map(skills.map((it) => [it.id, it]));
  // Chips: cada id seleccionado; si la skill ya no está en el registro (huérfana)
  // se muestra el propio id para no perder la selección.
  const selectedChips = selected.map((id) => ({ id, title: byId.get(id)?.title || id }));
  const hasSkills = skills.length > 0;

  return (
    <fieldset className="flex flex-col gap-3">
      <legend className="text-sm font-medium text-foreground">Skills disponibles</legend>

      {!hasSkills && selected.length === 0 ? (
        <p
          className="rounded-lg border border-dashed border-border px-3 py-2 text-xs text-muted-foreground"
          role="status">
          No hay skills registrados aún.
        </p>
      ) : (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              className="w-full justify-start text-muted-foreground sm:w-80"
              aria-label="Agregar skills"
              disabled={!hasSkills}>
              <HugeiconsIcon icon={Add01Icon} size={16} strokeWidth={1.5} aria-hidden="true" />
              Agregar skills…
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[min(24rem,calc(100vw-2rem))] p-0" align="start">
            <Command>
              <CommandInput placeholder="Buscar skills…" />
              <CommandList>
                <CommandEmpty>No hay skills que coincidan</CommandEmpty>
                {skills.map((item) => {
                  const isSelected = selected.includes(item.id);
                  return (
                    <CommandItem
                      key={item.id}
                      // cmdk busca por `value`; se incluyen título y descripción
                      // para que el filtro cliente abarque ambos.
                      value={`${item.title || item.id} ${item.description || ''}`}
                      onSelect={() => onToggle(item.id)}
                      // `group` + data-[selected] permite que el texto herede el
                      // color del estado resaltado (bg-accent/text-accent-foreground
                      // del CommandItem base) y no quede ilegible sobre el morado.
                      className="group items-start">
                      <HugeiconsIcon
                        icon={Tick02Icon}
                        size={16}
                        strokeWidth={2}
                        className={
                          isSelected
                            ? 'mt-0.5 text-primary group-data-[selected=true]:text-accent-foreground'
                            : 'mt-0.5 opacity-0'
                        }
                        aria-hidden="true"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium text-foreground group-data-[selected=true]:text-accent-foreground">
                          {item.title || item.id}
                        </span>
                        {item.description ? (
                          <span className="mt-0.5 block text-xs text-muted-foreground group-data-[selected=true]:text-accent-foreground/80">
                            {item.description}
                          </span>
                        ) : null}
                      </span>
                    </CommandItem>
                  );
                })}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      )}

      {selectedChips.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {selectedChips.map((chip) => (
            <Badge key={chip.id} variant="secondary" className="gap-1">
              {chip.title}
              <button
                type="button"
                onClick={() => onRemove(chip.id)}
                aria-label={`Quitar skill ${chip.title}`}
                className="flex h-3.5 w-3.5 items-center justify-center rounded-full text-secondary-foreground/70 transition-colors hover:text-secondary-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <HugeiconsIcon icon={Cancel01Icon} size={12} strokeWidth={2} aria-hidden="true" />
              </button>
            </Badge>
          ))}
        </div>
      ) : null}
    </fieldset>
  );
}

export default function InsightsAgents() {
  const [view, setView] = useState('list'); // 'list' | 'edit'
  const [listTab, setListTab] = useState('agentes'); // vista LISTA: 'agentes' | 'grupos'

  // --- Vista LISTA ---
  const [agents, setAgents] = useState([]);
  const [agentsState, setAgentsState] = useState({ loading: true, error: null });

  // --- Vista EDICION ---
  const [editing, setEditing] = useState(null); // agente en edicion o null (nuevo)
  const [name, setName] = useState('');
  const [defaultModel, setDefaultModel] = useState('');
  const [selection, setSelection] = useState({});
  const [filter, setFilter] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [formatRules, setFormatRules] = useState('');
  const [skills, setSkills] = useState([]);
  const [activeTab, setActiveTab] = useState('datos');
  const [saving, setSaving] = useState(false);

  // --- Registro de skills ---
  // Las skills reales vienen del toolkit: GET /toolkit/skills (paginado a 20).
  // Ya NO se consulta /toolkit/mcp en el flujo de agentes (mensaje 11: "solo
  // skills"). Cada item es {id, title, description} y el identificador estable
  // que se persiste es item.id.
  const [skillRegistry, setSkillRegistry] = useState([]);
  const [registryState, setRegistryState] = useState({ loading: true, error: null });

  const loadAgents = async () => {
    setAgentsState({ loading: true, error: null });
    try {
      const { data } = await api.get('/insights/agents');
      setAgents(Array.isArray(data) ? data : []);
      setAgentsState({ loading: false, error: null });
    } catch (e) {
      setAgentsState({ loading: false, error: apiError(e) });
    }
  };

  // Carga TODAS las skills del toolkit al montar la página, para que el combobox
  // filtre al instante (búsqueda cliente) sin esperar por red al escribir.
  // GET /toolkit/skills responde { items, total, page } con limit=20 hardcodeado
  // en el backend, así que se iteran páginas (page=1,2,…) hasta acumular `total`
  // o encontrar una página vacía, con tope defensivo MAX_SKILL_PAGES.
  // Edge cases (design.md C.1):
  //   - Falla la página 1 -> estado `error` con reintento.
  //   - Falla una página > 1 -> se conserva lo acumulado y se avisa con toast.
  const loadRegistry = async () => {
    setRegistryState({ loading: true, error: null });
    const toItems = (data) => (Array.isArray(data?.items) ? data.items : []);
    const pages = [];
    let page = 1;
    let total = Infinity;
    try {
      while (page <= MAX_SKILL_PAGES) {
        let res;
        try {
          res = await api.get('/toolkit/skills', { params: { page, limit: SKILLS_PAGE_LIMIT } });
        } catch (pageErr) {
          if (page === 1) throw pageErr; // sin nada cargado: error duro con reintento
          // Página intermedia: conservar lo acumulado y avisar sin romper.
          toast.error('No se pudieron cargar todas las skills');
          break;
        }
        const items = toItems(res.data);
        pages.push(items);
        if (typeof res.data?.total === 'number') total = res.data.total;
        const accumulated = accumulateSkillPages(pages).length;
        // Corta por página vacía (salvaguarda ante `total` inconsistente) o al
        // acumular el total informado.
        if (items.length === 0 || accumulated >= total) break;
        page += 1;
      }
      setSkillRegistry(accumulateSkillPages(pages));
      setRegistryState({ loading: false, error: null });
    } catch (e) {
      setSkillRegistry([]);
      setRegistryState({ loading: false, error: apiError(e) });
    }
  };

  useEffect(() => {
    loadAgents();
    loadRegistry();
  }, []);

  const openNew = () => {
    setEditing(null);
    setName('');
    setDefaultModel('');
    setSelection({});
    setFilter('');
    setSystemPrompt('');
    setFormatRules('');
    setSkills([]);
    setActiveTab('datos');
    setView('edit');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openEdit = (agent) => {
    setEditing(agent);
    setName(agent.name || '');
    setDefaultModel(agent.default_model || '');
    setSelection(resourcesToSelection(agent.resources || []));
    setFilter('');
    setSystemPrompt(agent.system_prompt || '');
    setFormatRules(agent.format_rules || '');
    setSkills(Array.isArray(agent.skills) ? agent.skills : []);
    setActiveTab('datos');
    setView('edit');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const backToList = () => {
    setView('list');
    setEditing(null);
  };

  const toggleSkill = (skillId) => {
    setSkills((prev) => (prev.includes(skillId) ? prev.filter((s) => s !== skillId) : [...prev, skillId]));
  };

  const removeSkill = (skillId) => {
    setSkills((prev) => prev.filter((s) => s !== skillId));
  };

  const resources = selectionToResources(selection);

  const guardar = async () => {
    const nombre = name.trim();
    if (!nombre) {
      toast.error('El nombre del agente es obligatorio');
      return;
    }
    setSaving(true);
    try {
      const body = {
        name: nombre,
        resources,
        // groups (legacy, JSONB de texto libre) queda deprecado: ya no se edita
        // desde la UI. Se envía vacío por compatibilidad del contrato del backend;
        // el acceso real vive en AgentAccessPanel (grupos reales + personas).
        groups: [],
        default_model: defaultModel.trim(),
        system_prompt: systemPrompt,
        format_rules: formatRules,
        skills,
      };
      if (editing) {
        await api.put(`/insights/agents/${editing.id}`, body);
        toast.success('Agente actualizado');
      } else {
        await api.post('/insights/agents', body);
        toast.success('Agente creado');
      }
      await loadAgents();
      backToList();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  // ===================== VISTA LISTA =====================
  if (view === 'list') {
    return (
      // py-10 da respiración sobre el header global morado (consistente con el resto de páginas)
      <div className="mx-auto max-w-5xl px-6 py-10">
        <PageHeader
          title="VIA Insights · Agentes"
          subtitle="Configura agentes de datos y los grupos de usuarios que pueden acceder a ellos."
          backTo="/insights"
          backLabel="Volver a VIA Insights"
        />

        <Tabs value={listTab} onValueChange={setListTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="agentes">Agentes</TabsTrigger>
            <TabsTrigger value="grupos">Grupos</TabsTrigger>
          </TabsList>

          <TabsContent value="agentes">
            <div className="mb-6 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={loadAgents}
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-secondary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label="Recargar agentes">
                <HugeiconsIcon icon={RefreshIcon} size={14} strokeWidth={1.5} aria-hidden="true" />
                Recargar
              </button>
              <Button type="button" onClick={openNew}>
                <HugeiconsIcon icon={Add01Icon} size={18} strokeWidth={1.5} aria-hidden="true" />
                Nuevo agente
              </Button>
            </div>

            {agentsState.loading ? (
              <div
                className="flex items-center gap-2 py-10 text-sm text-muted-foreground"
                role="status"
                aria-live="polite">
                <HugeiconsIcon
                  icon={Loading02Icon}
                  size={18}
                  strokeWidth={1.5}
                  className="animate-spin"
                  aria-hidden="true"
                />
                Cargando agentes…
              </div>
            ) : agentsState.error ? (
              <div
                className="flex items-center gap-2 rounded-lg bg-destructive/10 p-3 text-sm text-destructive"
                role="alert">
                <HugeiconsIcon icon={AlertCircleIcon} size={18} strokeWidth={1.5} aria-hidden="true" />
                <span className="flex-1">{agentsState.error}</span>
                <button type="button" onClick={loadAgents} className="font-medium hover:underline">
                  Reintentar
                </button>
              </div>
            ) : agents.length === 0 ? (
              <MotionSection className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border py-14 text-center">
                <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <HugeiconsIcon icon={DatabaseIcon} size={24} strokeWidth={1.5} aria-hidden="true" />
                </div>
                <p className="font-medium text-foreground">Aún no hay agentes</p>
                <p className="mt-1 max-w-sm text-sm text-muted-foreground">
                  Crea el primero: define qué datos puede consultar y cómo debe responder.
                </p>
                <Button type="button" onClick={openNew} className="mt-4">
                  <HugeiconsIcon icon={Add01Icon} size={18} strokeWidth={1.5} aria-hidden="true" />
                  Nuevo agente
                </Button>
              </MotionSection>
            ) : (
              <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {agents.map((agent) => (
                  <li key={agent.id}>
                    <Card className="h-full">
                      <CardContent className="flex h-full flex-col gap-3 p-4">
                        <div className="flex items-start gap-3">
                          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                            <HugeiconsIcon icon={DatabaseIcon} size={18} strokeWidth={1.5} aria-hidden="true" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate font-medium text-foreground">{agent.name}</p>
                            {/* Metadatos legibles: recursos con icono y modelo con
                                su nombre comercial + logo de proveedor (nunca el id
                                técnico crudo). */}
                            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                              <span className="inline-flex items-center gap-1">
                                <HugeiconsIcon icon={DatabaseIcon} size={13} strokeWidth={1.5} aria-hidden="true" />
                                {agent.resources?.length ?? 0} recurso(s)
                              </span>
                              {(() => {
                                const md = modelDisplay(agent.default_model);
                                if (!md) return null;
                                const iconName = md.provider ? PROVIDER_ICON[md.provider] : undefined;
                                return (
                                  <span className="inline-flex items-center gap-1">
                                    {iconName ? (
                                      <Icon icon={iconName} width={13} height={13} aria-hidden />
                                    ) : (
                                      <HugeiconsIcon
                                        icon={SparklesIcon}
                                        size={13}
                                        strokeWidth={1.5}
                                        aria-hidden="true"
                                      />
                                    )}
                                    {md.label}
                                  </span>
                                );
                              })()}
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => openEdit(agent)}
                            className="h-8 shrink-0"
                            aria-label={`Editar agente ${agent.name}`}>
                            <HugeiconsIcon icon={Edit02Icon} size={16} strokeWidth={1.5} aria-hidden="true" />
                            Editar
                          </Button>
                        </div>
                        {/* El acceso se gestiona por grupos reales y personas desde
                        la edición del agente (bloque «Acceso») y la pestaña
                        «Grupos»; el campo legacy groups ya no se muestra. */}
                      </CardContent>
                    </Card>
                  </li>
                ))}
              </ul>
            )}
          </TabsContent>

          <TabsContent value="grupos">
            <GroupsManager />
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  // ===================== VISTA EDICION =====================
  return (
    // py-10 da respiración sobre el header global morado (consistente con el resto de páginas)
    <div className="mx-auto max-w-5xl px-6 py-10">
      {/* El back aquí es el botón interno "Volver a la lista" (cambia de vista, no de ruta);
          por eso el PageHeader de esta vista no lleva backTo, para no duplicar navegación. */}
      <div className="mb-4">
        <button
          type="button"
          onClick={backToList}
          className="inline-flex items-center gap-1 rounded-md px-1 py-1 text-sm text-secondary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <HugeiconsIcon icon={ArrowLeft01Icon} size={16} strokeWidth={1.5} aria-hidden="true" />
          Volver a la lista
        </button>
      </div>

      <PageHeader
        title={editing ? `Editar agente${editing.name ? `: ${editing.name}` : ''}` : 'Nuevo agente'}
        subtitle="Define los datos que el agente puede consultar y las instrucciones que debe seguir."
      />

      <Card className="mb-6">
        <CardContent className="flex flex-col gap-4 p-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="agent-name" className="text-sm font-medium text-foreground">
                Nombre del agente
              </label>
              <Input
                id="agent-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ej.: Comercial"
                autoComplete="off"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label id="agent-model-label" className="text-sm font-medium text-foreground">
                Modelo por defecto
              </label>
              {/* Si el modelo guardado no está en la lista curada (valor legacy
                  o un id introducido manualmente antes), se añade como opción
                  extra para no perder la configuración existente. */}
              <Select value={defaultModel || ''} onValueChange={setDefaultModel}>
                <SelectTrigger id="agent-model" aria-labelledby="agent-model-label">
                  <SelectValue placeholder="Selecciona un modelo" />
                </SelectTrigger>
                <SelectContent>
                  {KNOWN_MODELS.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      <ModelOption provider={m.provider} label={m.label} />
                    </SelectItem>
                  ))}
                  {defaultModel && !KNOWN_MODELS.some((m) => m.id === defaultModel) ? (
                    <SelectItem value={defaultModel}>{defaultModel} (actual)</SelectItem>
                  ) : null}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Acceso: solo para un agente ya existente (uno nuevo se guarda primero y
          luego se gestiona su acceso). Concede acceso por grupo o por persona. */}
      {editing && (
        <Card className="mb-6">
          <CardContent className="flex flex-col gap-4 p-4">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <HugeiconsIcon icon={UserGroupIcon} size={18} strokeWidth={1.5} aria-hidden="true" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-foreground">Acceso</p>
                <p className="mt-0.5 text-sm text-muted-foreground">
                  Define qué grupos y personas pueden usar este agente.
                </p>
              </div>
            </div>
            <AgentAccessPanel agentId={editing.id} />
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="datos">Datos</TabsTrigger>
          <TabsTrigger value="instrucciones">Instrucciones</TabsTrigger>
        </TabsList>

        <TabsContent value="datos">
          <Card className="min-h-[24rem]">
            <CardContent className="p-4">
              {resources.length === 0 && (
                <p
                  className="mb-3 rounded-lg border border-dashed border-border px-3 py-2 text-xs text-muted-foreground"
                  role="status">
                  Aún no has seleccionado ningún esquema, tabla o columna. Puedes guardar igual y configurarlo luego.
                </p>
              )}
              <CatalogTree
                enableDescriptions
                selection={selection}
                onChange={setSelection}
                filter={filter}
                onFilterChange={setFilter}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="instrucciones">
          <Card>
            <CardContent className="flex flex-col gap-5 p-4">
              <MarkdownEditor
                id="agent-system-prompt"
                label="Instrucciones del agente"
                description="Cómo debe comportarse el agente: contexto, tono y reglas de negocio."
                value={systemPrompt}
                onChange={setSystemPrompt}
                rows={6}
                maxLength={4000}
                placeholder="Ej.: Eres un analista comercial. Responde con cifras verificables y cita las tablas usadas."
              />

              <MarkdownEditor
                id="agent-format-rules"
                label="Reglas de formato"
                description="Cómo entregar y formatear los resultados (tablas, unidades, moneda, redondeo)."
                value={formatRules}
                onChange={setFormatRules}
                rows={5}
                maxLength={4000}
                placeholder="Ej.: Montos en CLP con separador de miles. Fechas como DD-MM-AAAA."
              />

              <SkillsCombobox
                state={registryState}
                skills={skillRegistry}
                selected={skills}
                onToggle={toggleSkill}
                onRemove={removeSkill}
                onRetry={loadRegistry}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="mt-6 flex items-center justify-end gap-2">
        <Button type="button" variant="ghost" onClick={backToList} disabled={saving}>
          <HugeiconsIcon icon={Cancel01Icon} size={18} strokeWidth={1.5} aria-hidden="true" />
          Cancelar
        </Button>
        <Button type="button" onClick={guardar} disabled={saving}>
          <HugeiconsIcon
            icon={saving ? Loading02Icon : FloppyDiskIcon}
            size={18}
            strokeWidth={1.5}
            className={saving ? 'animate-spin' : ''}
            aria-hidden="true"
          />
          {editing ? 'Guardar cambios' : 'Crear agente'}
        </Button>
      </div>
    </div>
  );
}
